// Client-side defense-in-depth mirror of the server's
// FileService.ValidateFileUpload scan. Runs on the ArrayBuffer the
// upload component already reads for mime detection, so it costs
// nothing extra beyond the pattern search.
//
// This is NOT a substitute for the server-side scan: an attacker
// using Burp Suite / curl skips the browser entirely and posts
// directly to the API. The server scan remains authoritative. This
// layer only adds:
//   * instant rejection at drop-time (no wait for async worker)
//   * fewer bytes moved to blob storage on obvious payloads
//   * clearer UX message to honest users who dropped the wrong file
//
// Keep the marker/pattern lists in sync with the server-side lists
// in RSM.Tsa.Standard.Services/FileService.cs -- if either drifts,
// server-side wins.

// Unambiguous PDF actions with no legitimate business use in a
// user-uploaded document. Presence of either of these strings in
// the file is enough to reject regardless of context.
const PDF_HARD_BLOCK_MARKERS = [
  '/Launch',       // launches an external process; not used by tax/form PDFs
  '/EmbeddedFile', // embedded-file dictionary can hide arbitrary payloads
]

// Dangerous Adobe JavaScript APIs. These are the specific patterns
// weaponized PDFs use for exfiltration / RCE / credential theft.
// Legitimate Adobe form calculation scripts (AFDate_FormatEx,
// AFNumber_Format, AFSpecial_Keystroke, event.value, etc.) never
// use these APIs, so scanning for THEM instead of the mere presence
// of /JavaScript eliminates the false-positive class we saw on IRS
// Signature Authorization forms while still catching real threats.
const DANGEROUS_JS_APIS = [
  'launchURL(',            // Adobe app.launchURL / this.launchURL -- opens external URLs
  'util.printf(',          // CVE-2008-2992 format-string buffer overflow
  'getIcon(',              // CVE-2009-0927 (Collab.getIcon) family
  'Collab.',               // Collab.* namespace -- exploited in multiple CVEs
  'plugIns.getPlugIn(',    // plugin abuse (CVE-2009-1859, others)
  'syncAnnotScan(',        // CVE-2007-5659
  'mailDoc(',              // silent email of the document -- data exfil
  'submitForm(',           // form data POST to attacker URL (paired with newDoc / crafted forms)
  'newDoc(',               // programmatic new-document creation (attack primitives)
  'importDataObject(',     // import external data into the PDF at runtime
  'saveAs(',               // programmatic save of arbitrary content
  'exportAsFDF(',          // export form data to attacker-controlled FDF
  'exportAsXFDF(',         // XFDF variant
  'this.print(',           // silent print / print-to-remote-server abuse
]

// Also flag documents that fire JavaScript on file open (no user
// interaction required). Legitimate forms put JS inside per-field
// /AA (Additional Actions) triggered only when the user focuses /
// changes a field. Attackers use /OpenAction referencing a JS action
// so the payload runs the instant the reader opens the file. This
// pattern is the pen-tester's exact PoC shape.
const OPEN_ACTION_JS_PATTERN = /\/OpenAction[^]{0,200}?\/JavaScript/

const CSV_FORMULA_PATTERNS = [
  'cmd|',           // DDE (=cmd|'/C calc'!A0)
  'DDE(',
  'RTD(',
  'CALL(',
  'WEBSERVICE(',
  'FILTERXML(',
  'IMPORTDATA(',
  'IMPORTHTML(',
  'IMPORTXML(',
  'IMPORTRANGE(',
  'IMPORTFEED(',
  'HYPERLINK(',
  'GOOGLEFINANCE(',
]

// "%PDF" — PDF magic bytes.
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46]

/**
 * Scan an uploaded file's raw bytes for known-dangerous content.
 * Returns null when the file passes, or a short human-readable
 * reason when it should be rejected.
 */
export function scanForDangerousContent(
  buffer: ArrayBuffer,
  filename: string
): string | null {
  if (!buffer || buffer.byteLength === 0) {
    return null
  }
  const bytes = new Uint8Array(buffer)
  const ext = getExtension(filename).toLowerCase()

  // Trigger on .pdf extension OR "%PDF" magic anywhere in the first
  // 1024 bytes (matches the server-side broadened trigger that
  // closed the "shift %PDF off offset 0" pen-tester bypass).
  const isPdf =
    ext === '.pdf' ||
    findBytes(bytes, PDF_MAGIC, 0, Math.min(1024, bytes.length)) >= 0
  if (isPdf) {
    const text = decodeLatin1(bytes)

    // Hard-block markers first.
    for (const marker of PDF_HARD_BLOCK_MARKERS) {
      if (text.indexOf(marker) >= 0) {
        return 'contains a dangerous PDF action (Launch or EmbeddedFile)'
      }
    }

    // Dangerous JavaScript APIs anywhere in the file (raw text). If
    // the file has JS, it will appear either inside a /JS(...) literal
    // in the raw bytes, or inside a compressed /ObjStm we can't see
    // from the browser. Client-side we do the raw-bytes pass only;
    // the server-side scanner decompresses streams for the deep pass.
    for (const api of DANGEROUS_JS_APIS) {
      if (text.indexOf(api) >= 0) {
        return 'contains dangerous JavaScript API calls'
      }
    }

    // /OpenAction pointing at a /JavaScript action (fires on file
    // open) -- matches the pen-tester's PoC shape.
    if (OPEN_ACTION_JS_PATTERN.test(text)) {
      return 'contains JavaScript that fires when the file is opened'
    }
  }

  // CSV / plain-text formula injection scan.
  if (ext === '.csv' || ext === '.txt') {
    const text = decodeLatin1(bytes).toLowerCase()
    for (const pattern of CSV_FORMULA_PATTERNS) {
      if (text.indexOf(pattern.toLowerCase()) >= 0) {
        return 'contains dangerous spreadsheet formula'
      }
    }
  }

  // Excel (.xlsx / .xlsm / .xls) formula scan requires parsing the
  // ZIP + XML (formulas live inside compressed cells). Left to the
  // server-side EPPlus walk. Client-side scan of Excel raw bytes
  // wouldn't find compressed formulas anyway.
  return null
}

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.')
  return idx >= 0 ? filename.substring(idx) : ''
}

function decodeLatin1(bytes: Uint8Array): string {
  // Latin-1 maps bytes 0..255 to identical code points -- lets us
  // string-search for ASCII markers without Unicode surprises on
  // binary content.
  return new TextDecoder('latin1').decode(bytes)
}

function findBytes(
  haystack: Uint8Array,
  needle: number[],
  start: number,
  endExclusive: number
): number {
  const limit = Math.min(endExclusive, haystack.length) - needle.length
  for (let i = start; i <= limit; i++) {
    let j = 0
    while (j < needle.length && haystack[i + j] === needle[j]) j++
    if (j === needle.length) return i
  }
  return -1
}
